import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import 'moment-timezone';
import CommonUtils from '../../util/CommonUtils';
import { useSearchParams } from 'react-router-dom';
import API from '../../service/SiteKickAPIService';
import { Modal, Input, message } from 'antd';
import SessionManager from '../../routes/SessionManager';
import Loading from '../../components/Loading';

// Configure message to appear above modals
message.config({
    top: 100,
    duration: 3,
    maxCount: 3,
    getContainer: () => document.body,
});

const SecurityIncidentReport = ({ siteId: propSiteId, dateFrom: propDateFrom, dateTo: propDateTo, isEmbedded = false, onSendReportReady = null }) => {

    const [searchParams] = useSearchParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailModalVisible, setEmailModalVisible] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);

    const [reportData, setReportData] = useState({
        // Customer & Property Information
        customerName: '',
        siteName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        superintendentName: '',
        superintendentEmail: '',
        superintendentPhone: '',
        projectManagerName: '',
        projectManagerEmail: '',
        projectManagerPhone: '',

        // Incident Details
        incidentDate: '',
        incidentTime: '',
        reportedBy: '',
        incidentType: 'Intruders on site',
        locationOnProperty: '',
        descriptionOfFacts: '',
        immediateActionsTaken: '',
        respondingOfficer: '',
        caseNumber: '',

        // System & Response Checklist
        camerasOperational: true,
        afterHoursScheduleActivated: true,
        intrusionDetected: true,
        monitoringCenterNotified: true,
        callListFollowed: true,
        policeDispatched: true,

        // System notes
        camerasNotes: 'All cameras operational',
        scheduleNotes: 'Schedule activated',
        intrusionNotes: '',
        monitoringNotes: '',
        callListNotes: 'See Exhibit A',
        policeNotes: 'See Exhibit A',

        // Activity Log
        activityLog: [],

        // Report metadata
        preparedBy: 'SiteKick Monitoring Team',
        preparedTitle: 'Finance & Operations',
        reviewDate: moment().format('YYYY-MM-DD'),
        companyYear: new Date().getFullYear(),
        website: 'www.sitekick.com',
        supportEmail: 'support@sitekick.com',
        phone: '612-605-3170'
    });

    // ----------------------------------------------------
    // Button Handlers
    // ----------------------------------------------------
    const handleSendReport = useCallback(() => {
        console.log('handleSendReport called!');

        // Pre-populate with superintendent and project manager emails
        const defaultEmails = [
            reportData.superintendentEmail,
            reportData.projectManagerEmail
        ].filter(email => email).join(', ');

        console.log('Setting email recipients to:', defaultEmails);
        setEmailRecipients(defaultEmails);
        setEmailModalVisible(true);
    }, [reportData.superintendentEmail, reportData.projectManagerEmail]);

    // Debug: log when modal state changes
    useEffect(() => {
        console.log('emailModalVisible changed to:', emailModalVisible);
    }, [emailModalVisible]);

    // Expose handleSendReport to parent when embedded
    useEffect(() => {
        if (isEmbedded && onSendReportReady && !loading) {
            console.log('Exposing handleSendReport to parent modal');
            onSendReportReady(handleSendReport);
        }
    }, [isEmbedded, onSendReportReady, loading, handleSendReport]);

    async function sendReportEmail() {
        console.log('sendReportEmail called!');
        console.log('emailRecipients:', emailRecipients);

        if (!emailRecipients || !emailRecipients.trim()) {
            console.log('No email recipients provided');
            message.error('Please enter at least one email address');
            return;
        }

        // Store email list before closing modal
        const emailList = emailRecipients.split(',').map(email => email.trim()).filter(email => email);
        console.log('Email list:', emailList);

        console.log('About to close modal and send email to:', emailList);
        // Close modal first
        setEmailModalVisible(false);
        setSendingEmail(true);

        // Show loading message
        message.loading('Sending report...', 0);

        // Wait for modal to fully close
        console.log('Waiting 300ms for modal to close...');
        await new Promise(resolve => setTimeout(resolve, 300));

        try {
            console.log('Looking for .report-container element...');
            // Clone the report container to avoid modifying the visible DOM
            const reportElement = document.querySelector('.report-container');
            console.log('Report element found:', !!reportElement);

            if (!reportElement) {
                throw new Error('Report container not found');
            }

            console.log('Cloning report...');
            const clonedReport = reportElement.cloneNode(true);

            // Convert all textareas to plain text divs in the CLONE
            const textareas = clonedReport.querySelectorAll('textarea');
            console.log('Found textareas:', textareas.length);

            textareas.forEach(textarea => {
                const value = textarea.value;
                const textDiv = document.createElement('div');
                textDiv.textContent = value;
                textDiv.style.whiteSpace = 'pre-wrap';
                textDiv.style.wordBreak = 'break-word';
                textDiv.style.fontSize = textarea.style.fontSize || '14px';
                textDiv.style.fontFamily = textarea.style.fontFamily || 'Arial, Helvetica, sans-serif';
                textDiv.style.padding = textarea.style.padding || '8px';
                textarea.parentNode.replaceChild(textDiv, textarea);
            });

            // Get the HTML content from the clone
            const reportContent = clonedReport.outerHTML;
            console.log('Report content length:', reportContent.length);

            // Prepare email payload
            const payload = {
                form_input: {
                    recipients: emailList,
                    subject: `Security Incident Report - ${reportData.siteName} - ${reportData.incidentDate}`,
                    email_body: reportContent
                }
            };

            console.log('Sending email with payload...');

            const response = await CommonUtils.ajax({
                url: API.EMAIL.SEND,
                type: 'POST',
                dataType: 'json',
                contentType: 'application/json',
                data: JSON.stringify(payload)
            });

            console.log('Email send response:', response);

            // Destroy the loading message
            message.destroy();

            if (response && response.status && response.status.code === 200) {
                message.success('Report sent successfully!', 3);
                window.status = 'ready';
            } else {
                throw new Error('Failed to send email');
            }

        } catch (err) {
            console.error('Error sending report email:', err);
            console.error('Error stack:', err.stack);

            // Destroy the loading message
            message.destroy();
            message.error('Failed to send report. Please try again.', 5);
        } finally {
            console.log('Resetting sendingEmail state');
            setSendingEmail(false);
        }
    }

    // ----------------------------------------------------
    // API: Fetch BUC Activity Log
    // ----------------------------------------------------
    function fetchBUCActivityLog(site_id, dateFrom, dateTo) {
        var payload = {
            form_input: {
                query: JSON.stringify({
                    attributes: ['utcTime', "DATE_FORMAT(siteTime, '%Y-%m-%d %H:%i:%s') siteTime", 'eventNo', 'site_id', 'record'],
                    object: 'whirc_incidents_view',
                    filters: [
                        { attribute: 'site_id', value: site_id, operator: '=' },
                        { join_by: 'and', attribute: 'siteTime', operator: '>=', value: moment(dateFrom).startOf('day').format('YYYY-MM-DD HH:mm:ss') },
                        { join_by: 'and', attribute: 'siteTime', operator: '<=', value: moment(dateTo).endOf('day').format('YYYY-MM-DD HH:mm:ss') }
                    ],
                    pagination: { page: 1, size: 500 },
                    sort_by: [{ attribute: 'siteTime', order: 'asc' }]
                })
            }
        };

        console.log('Fetching BUC Activity Log with payload:', payload);

        CommonUtils.ajax({
            url: API.DATA.EXPLORER,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(payload)
        }).then(function (res) {
            console.log('BUC Activity Log Response:', res);

            if (res && res.status && res.status.code === 200 && res.data && res.data.length > 0) {
                var details = res.data;
                console.log('Processing activity log data, count:', details.length);

                // Filter to only show main signal/alarm rows (rowType "S" or "D")
                var filteredDetails = details.filter(function (item) {
                    return item.record && (item.record.rowType === 'S' || item.record.rowType === 'D');
                });

                var activityLog = filteredDetails.map(function (item) {
                    return {
                        rowType: item.record.rowType || '',
                        sequenceNo: item.record.sequenceNo,
                        eventNo: item.eventNo || '',
                        timestamp: item.siteTime || '',
                        activity: (item.record && item.record.text) || ''
                    };
                });

                console.log('Setting activity log with entries:', activityLog.length);

                // Get the first alarm entry to set incident date/time
                var firstAlarm = details.find(function (item) {
                    return item.record && item.record.activityType === 1 && item.record.rowType === 'S'; // activityType 1 = Alarm
                });

                if (!firstAlarm && details.length > 0) {
                    // If no alarm found, use the first entry
                    firstAlarm = details[0];
                }

                var incidentDateTime = firstAlarm ? moment(firstAlarm.siteTime) : null;
                var incidentLocation = firstAlarm && firstAlarm.record && firstAlarm.record.pointId ? firstAlarm.record.pointId : '';

                setReportData(function (prev) {
                    return {
                        ...prev,
                        activityLog: activityLog,
                        incidentDate: incidentDateTime ? incidentDateTime.format('YYYY-MM-DD') : prev.incidentDate,
                        incidentTime: incidentDateTime ? incidentDateTime.format('HH:mm') + ' CST' : prev.incidentTime,
                        locationOnProperty: incidentLocation || prev.locationOnProperty,
                        intrusionNotes: incidentDateTime ? 'Intruders detected at ' + incidentDateTime.format('HH:mm') : prev.intrusionNotes,
                        monitoringNotes: incidentDateTime ? 'Notification at ' + incidentDateTime.add(1, 'minutes').format('HH:mm') : prev.monitoringNotes
                    };
                });
            } else {
                console.warn('No activity log details found in response');
            }
        }).catch(function (err) {
            console.error('Error fetching BUC activity log:', err);
            console.error('Error details:', err.responseText || err.message);
        });
    }

    // ----------------------------------------------------
    // API: Fetch User Details
    // ----------------------------------------------------
    async function getUserDetails(userLogin) {
        if (!userLogin) return null;

        try {
            const payload = {
                form_input: {
                    query: JSON.stringify({
                        attributes: ['user_login', 'JSON_EXTRACT(record, "$.user_name") AS user_name', 'JSON_EXTRACT(record, "$.user_email1") AS user_email', 'JSON_EXTRACT(record, "$.user_phone1") AS user_mobile'],
                        object: 'user',
                        filters: [
                            { attribute: 'user_login', value: userLogin, operator: '=' }
                        ],
                        pagination: { page: 1, size: 1 },
                        sort_by: [{ attribute: 'user_login', order: 'asc' }]
                    })
                }
            };

            const res = await CommonUtils.ajax({
                url: API.DATA.EXPLORER,
                type: 'POST',
                dataType: 'json',
                contentType: 'application/json',
                data: JSON.stringify(payload)
            });

            if (res && res.status && res.status.code === 200 && res.data && res.data.length > 0) {
                return res.data[0];
            }
            return null;
        } catch (err) {
            console.error('Error fetching user details:', err);
            return null;
        }
    }

    async function getSite(site_id) {
        let criteria = { "from": 0, "size": 1, site_id: site_id };
        try {
            return await new Promise((resolve, reject) => {
                CommonUtils.ajax({
                    url: API.COMMON_TABLE_OPERATIONS.SITE.FETCH,
                    type: 'POST',
                    contentType: "application/json; charset=utf-8",
                    dataType: "json",
                    data: JSON.stringify({
                        source: 'D',
                        criteria: criteria
                    })
                }).then(
                    (res) => {
                        if (res.status) {
                            if (res.status.code === 200) {
                                var siteRecord = res.data && res.data.records && res.data.records.length > 0 ? res.data.records[0] : null;
                                // The site data is in the 'record' property
                                resolve(siteRecord && siteRecord.record ? siteRecord.record : siteRecord);
                            } else
                                reject(null);
                        } else {
                            reject(null);
                        }
                    },
                    (error) => {
                        reject(null);
                    }
                );
            });
        } catch (err) {
            console.error('Error in getSite:', err);
            return null;
        }
    }

    // ----------------------------------------------------
    // STEP 1: INIT USER + SITE
    // ----------------------------------------------------
    useEffect(function () {
        document.body.style.background = 'white';
        async function init() {
            try {
                var params = {};

                // Use props if available (when opened from modal), otherwise use URL params
                if (propSiteId) {
                    params = {
                        site_id: propSiteId,
                        date_from: propDateFrom,
                        date_to: propDateTo
                    };
                } else {
                    searchParams.forEach(function (val, key) {
                        params[key] = val;
                    });

                    params = {
                        ...params,
                        user_login: 'sitekick_messenger',
                        user_token: 'e529f42c-dd1d-443a-bbf2-65cf69443b6f',
                        user_type: 'user'
                    };
                }

                // Get user from session or create from params
                let sessionManager = new SessionManager();
                var userData = sessionManager.readCookie('user', false);

                if (!userData && params.user_login && params.user_token) {
                    userData = {
                        user_login: params.user_login,
                        user_token: params.user_token,
                        user_type: params.user_type || 'user'
                    };
                }

                setUser(userData);

                var site = await getSite(params.site_id);
                var customer = await CommonUtils.getCustomer(site.customer_id, user || userData);

                // Fetch superintendent and project manager details from user table
                var superintendentDetails = null;
                var projectManagerDetails = null;

                if (site.site_superintendent) {
                    superintendentDetails = await getUserDetails(site.site_superintendent);
                }

                if (site.project_manager) {
                    projectManagerDetails = await getUserDetails(site.project_manager);
                }

                // Update report data with site/customer info
                setReportData(function (prev) {
                    return {
                        ...prev,
                        customerName: customer.customer_busname || '',
                        siteName: site.site_name || '',
                        address: site.site_address1 || '',
                        city: site.site_city || '',
                        state: site.site_stateprov || '',
                        zip: site.site_postal || '',
                        superintendentName: superintendentDetails ? superintendentDetails.user_name : (site.site_superintendent || ''),
                        superintendentEmail: superintendentDetails ? superintendentDetails.user_email : (site.site_superintendent_email || ''),
                        superintendentPhone: superintendentDetails ? superintendentDetails.user_mobile : (site.site_superintendent_mobile || site.site_superintendent_phone || ''),
                        projectManagerName: projectManagerDetails ? projectManagerDetails.user_name : (site.project_manager || ''),
                        projectManagerEmail: projectManagerDetails ? projectManagerDetails.user_email : (site.project_manager_email || ''),
                        projectManagerPhone: projectManagerDetails ? projectManagerDetails.user_mobile : (site.project_manager_phone || ''),
                        reportedBy: superintendentDetails ? superintendentDetails.user_name : (site.site_superintendent || '')
                    };
                });

                // Fetch BUC activity log if buc_id and dates are provided
                if (params.site_id && params.date_from && params.date_to) {
                    fetchBUCActivityLog(params.site_id, params.date_from, params.date_to);
                } else {
                    console.warn('BUC Activity Log not fetched: missing buc_id or date_from parameters');
                }

                setTimeout(() => {
                    setLoading(false);
                    signalReady();
                }, 1000);

            } catch (e) {
                console.error("INIT ERROR:", e);
                setLoading(false);
                window.status = 'ready';
            }
        }

        init();
    }, [propSiteId, propDateFrom, propDateTo]);

    function signalReady() {
        setTimeout(() => {
            // Don't clear document.head when embedded in a modal
            if (!isEmbedded) {
                document.head.innerHTML = '';
            }
            window.status = 'ready';
        }, 2000);
    }

    const sectionStyle = {
        padding: '20px 30px',
        borderBottom: '1px solid #e8e8e8'
    };

    const h2Style = {
        fontWeight: 'bold',
        margin: '0 0 15px 0',
        fontSize: '16px',
        color: 'white',
        padding: '10px 15px',
        background: '#6b33b4',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
        marginTop: '15px'
    };

    const labelStyle = {
        padding: '10px 0',
        fontSize: '14px',
        width: '200px',
        color: '#000000',
        fontWeight: '600',
        verticalAlign: 'top'
    };

    const valueStyle = {
        padding: '10px 10px',
        fontSize: '14px',
        color: '#333333'
    };

    const checklistTableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
        marginTop: '15px',
        border: '1px solid #e8e8e8'
    };

    const checklistHeaderStyle = {
        backgroundColor: '#deccf2',
        color: '#000000',
        padding: '12px',
        textAlign: 'center',
        fontWeight: 'bold',
        border: '1px solid #e8e8e8'
    };

    const checklistCellStyle = {
        padding: '12px',
        border: '1px solid #e8e8e8',
        textAlign: 'left'
    };

    const checklistLabelStyle = {
        padding: '12px',
        border: '1px solid #e8e8e8',
        textAlign: 'left',
        fontWeight: '500'
    };

    const activityLogStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px',
        marginTop: '15px',
        border: '1px solid #e8e8e8'
    };

    const activityHeaderStyle = {
        backgroundColor: '#f0f0f0',
        padding: '8px',
        border: '1px solid #e8e8e8',
        fontWeight: 'bold',
        textAlign: 'left'
    };

    const activityCellStyle = {
        padding: '8px',
        border: '1px solid #e8e8e8',
        verticalAlign: 'top'
    };

    const footerStyle = {
        padding: '30px',
        backgroundColor: '#fafafa',
        borderRadius: '8px'
    };

    const responsiveStyles = `
        * {
            box-sizing: border-box;
        }

        .ant-message {
            z-index: 25000 !important;
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        @media only screen and (max-width: 600px) {
            .report-container {
                margin: 5px !important;
            }

            .report-section {
                padding: 15px !important;
            }

            .property-label {
                width: auto !important;
                display: block !important;
                padding-bottom: 5px !important;
            }

            .property-value {
                display: block !important;
                padding-left: 0 !important;
            }

            .checklist-table {
                font-size: 11px !important;
            }

            .activity-log {
                font-size: 10px !important;
            }
        }

        @media print {
            body {
                background: white !important;
            }
            .report-container {
                margin: 0 !important;
                box-shadow: none !important;
            }
        }
    `;

    return (
        <React.Fragment>
            <style dangerouslySetInnerHTML={{ __html: responsiveStyles }} />


            {loading ?
                <center><Loading className="mt-5">Loading Security Incident Report...</Loading></center> :
                <div className="report-container" style={{
                    position: 'relative',
                    borderRadius: '8px',
                    margin: '10px',
                    padding: '0',
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    maxWidth: '100%'
                }}>

                    <div style={{
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0'
                    }}>

                        {/* Header */}
                        <div className="report-header" style={{
                            backgroundColor: '#f5f5f5',
                            padding: '40px 50px',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            borderBottom: '1px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            position: 'relative'
                        }}>
                            <table class="logo-table" style={{ fontWeight: 'bold', textAlign: 'center', width: "100%", borderCollapse: "collapse", fontSize: "18px", color: "#6b33b4" }}>
                                <tbody>
                                    <tr>
                                        <td style={{ "width": "50px" }}>
                                            <span>logo</span>
                                        </td>
                                        <td>
                                            WHIRC Daily Incident Report
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Customer & Property Information */}
                        <div className="report-section" style={sectionStyle}>
                            <h2 style={h2Style}>Customer & Property Information</h2>
                            <table style={tableStyle}>
                                <tbody>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Customer Name:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.customerName}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Site / Property Name:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.siteName}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Address:</td>
                                        <td className="property-value" style={valueStyle}>
                                            {reportData.address}<br />
                                            {reportData.city}, {reportData.state} {reportData.zip}
                                        </td>
                                    </tr>
                                    {
                                        reportData.superintendentName && <tr>
                                            <td className="property-label" style={labelStyle}>Superintendent Contact:</td>
                                            <td className="property-value" style={valueStyle}>
                                                {reportData.superintendentName}<br />
                                                {reportData.superintendentEmail}<br />
                                                Phone: {reportData.superintendentPhone}
                                            </td>
                                        </tr>
                                    }
                                    {
                                        reportData.projectManagerName && <tr>
                                            <td className="property-label" style={labelStyle}>Project Manager Contact:</td>
                                            <td className="property-value" style={valueStyle}>
                                                {reportData.projectManagerName}<br />
                                                {reportData.projectManagerEmail}<br />
                                                Phone: {reportData.projectManagerPhone}
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>

                        {/* Incident Details */}
                        <div className="report-section" style={sectionStyle}>
                            <h2 style={h2Style}>Incident Details</h2>
                            <table style={tableStyle}>
                                <tbody>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Date of Incident:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.incidentDate}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Time of Incident:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.incidentTime}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Reported By:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.reportedBy}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Incident Type:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.incidentType}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Location on Property:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.locationOnProperty}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Description of Facts:</td>
                                        <td className="property-value" style={valueStyle}>
                                            <textarea
                                                value={reportData.descriptionOfFacts}
                                                onChange={(e) => setReportData({ ...reportData, descriptionOfFacts: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '100px',
                                                    padding: '8px',
                                                    fontSize: '14px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    resize: 'vertical'
                                                }}
                                                placeholder="Enter description of facts..."
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Immediate Actions Taken:</td>
                                        <td className="property-value" style={valueStyle}>
                                            <textarea
                                                value={reportData.immediateActionsTaken}
                                                onChange={(e) => setReportData({ ...reportData, immediateActionsTaken: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '100px',
                                                    padding: '8px',
                                                    fontSize: '14px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    resize: 'vertical'
                                                }}
                                                placeholder="Enter immediate actions taken..."
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Responding Officer / ID:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.respondingOfficer || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Report / Case Number:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.caseNumber || 'Unknown'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* System & Response Checklist */}
                        <div className="report-section" style={sectionStyle}>
                            <h2 style={h2Style}>System & Response Checklist</h2>
                            <table className="checklist-table" style={checklistTableStyle}>
                                <thead>
                                    <tr>
                                        <th style={{ ...checklistHeaderStyle, width: '40px' }}>Y</th>
                                        <th style={{ ...checklistHeaderStyle, width: '40px' }}>N</th>
                                        <th style={{ ...checklistHeaderStyle, textAlign: 'left' }}>Systems & Processes</th>
                                        <th style={{ ...checklistHeaderStyle, textAlign: 'left' }}>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ backgroundColor: reportData.camerasOperational ? '#90EE90' : '#ffffff' }}>
                                        <td style={checklistCellStyle}>{reportData.camerasOperational ? '✓' : ''}</td>
                                        <td style={checklistCellStyle}>{!reportData.camerasOperational ? '✓' : ''}</td>
                                        <td style={checklistLabelStyle}>Cameras Operational</td>
                                        <td style={checklistCellStyle}>
                                            <textarea
                                                value={reportData.camerasNotes}
                                                onChange={(e) => setReportData({ ...reportData, camerasNotes: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    padding: '6px',
                                                    fontSize: '12px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                    <tr style={{ backgroundColor: reportData.afterHoursScheduleActivated ? '#90EE90' : '#ffffff' }}>
                                        <td style={checklistCellStyle}>{reportData.afterHoursScheduleActivated ? '✓' : ''}</td>
                                        <td style={checklistCellStyle}>{!reportData.afterHoursScheduleActivated ? '✓' : ''}</td>
                                        <td style={checklistLabelStyle}>After-Hours Schedule Activated</td>
                                        <td style={checklistCellStyle}>
                                            <textarea
                                                value={reportData.scheduleNotes}
                                                onChange={(e) => setReportData({ ...reportData, scheduleNotes: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    padding: '6px',
                                                    fontSize: '12px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                    <tr style={{ backgroundColor: reportData.intrusionDetected ? '#90EE90' : '#ffffff' }}>
                                        <td style={checklistCellStyle}>{reportData.intrusionDetected ? '✓' : ''}</td>
                                        <td style={checklistCellStyle}>{!reportData.intrusionDetected ? '✓' : ''}</td>
                                        <td style={checklistLabelStyle}>Intrusion Detected by Software</td>
                                        <td style={checklistCellStyle}>
                                            <textarea
                                                value={reportData.intrusionNotes}
                                                onChange={(e) => setReportData({ ...reportData, intrusionNotes: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    padding: '6px',
                                                    fontSize: '12px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                    <tr style={{ backgroundColor: reportData.monitoringCenterNotified ? '#90EE90' : '#ffffff' }}>
                                        <td style={checklistCellStyle}>{reportData.monitoringCenterNotified ? '✓' : ''}</td>
                                        <td style={checklistCellStyle}>{!reportData.monitoringCenterNotified ? '✓' : ''}</td>
                                        <td style={checklistLabelStyle}>Monitoring Center Notified</td>
                                        <td style={checklistCellStyle}>
                                            <textarea
                                                value={reportData.monitoringNotes}
                                                onChange={(e) => setReportData({ ...reportData, monitoringNotes: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    padding: '6px',
                                                    fontSize: '12px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                    <tr style={{ backgroundColor: reportData.callListFollowed ? '#90EE90' : '#ffffff' }}>
                                        <td style={checklistCellStyle}>{reportData.callListFollowed ? '✓' : ''}</td>
                                        <td style={checklistCellStyle}>{!reportData.callListFollowed ? '✓' : ''}</td>
                                        <td style={checklistLabelStyle}>Call List Followed by Monitoring Center</td>
                                        <td style={checklistCellStyle}>
                                            <textarea
                                                value={reportData.callListNotes}
                                                onChange={(e) => setReportData({ ...reportData, callListNotes: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    padding: '6px',
                                                    fontSize: '12px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                    <tr style={{ backgroundColor: reportData.policeDispatched ? '#90EE90' : '#ffffff' }}>
                                        <td style={checklistCellStyle}>{reportData.policeDispatched ? '✓' : ''}</td>
                                        <td style={checklistCellStyle}>{!reportData.policeDispatched ? '✓' : ''}</td>
                                        <td style={checklistLabelStyle}>Police Dispatched</td>
                                        <td style={checklistCellStyle}>
                                            <textarea
                                                value={reportData.policeNotes}
                                                onChange={(e) => setReportData({ ...reportData, policeNotes: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '60px',
                                                    padding: '6px',
                                                    fontSize: '12px',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '3px',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Activity Log - Exhibit A */}
                        <div className="report-section" style={sectionStyle}>
                            <h2 style={h2Style}>Exhibit A - Alarm Monitoring Center Activity Log</h2>
                            {reportData.activityLog && reportData.activityLog.length > 0 ? (
                                <table className="activity-log" style={activityLogStyle}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                                            <th style={{ ...activityHeaderStyle, width: '150px' }}>Date/Time</th>
                                            <th style={activityHeaderStyle}>Activity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.activityLog.map((log, index) => (
                                            <React.Fragment key={index}>{
                                                log.rowType === 'S' &&
                                                <tr style={{ backgroundColor: '#f9f9f9' }}>
                                                    <td colSpan={2} style={activityCellStyle}><b>Event: {log.eventNo}</b></td>
                                                </tr>
                                            }
                                                <tr style={index % 2 === 1 ? { backgroundColor: '#f9f9f9' } : {}}>
                                                    <td style={activityCellStyle}>{log.timestamp}</td>
                                                    <td style={activityCellStyle}>{log.activity}</td>
                                                </tr>
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{
                                    padding: '20px',
                                    backgroundColor: '#f9f9f9',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '4px',
                                    textAlign: 'center',
                                    fontStyle: 'italic',
                                    color: '#999'
                                }}>
                                    {loading ? 'Loading activity log...' : 'No activity log data available. Please provide buc_id and date parameters.'}
                                </div>
                            )}
                        </div>

                        {/* Review & Sign-off */}
                        <div className="report-section" style={sectionStyle}>
                            <h2 style={h2Style}>Review & Sign-Off</h2>
                            <table style={tableStyle}>
                                <tbody>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Report Prepared By:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.preparedBy}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Title:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.preparedTitle}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Date of Review:</td>
                                        <td className="property-value" style={valueStyle}>{reportData.reviewDate}</td>
                                    </tr>
                                    <tr>
                                        <td className="property-label" style={labelStyle}>Client Signature:</td>
                                        <td className="property-value" style={valueStyle}>
                                            <div style={{
                                                borderBottom: '1px solid #000',
                                                width: '250px',
                                                height: '40px',
                                                marginTop: '10px'
                                            }}></div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="footer-section" style={footerStyle}>
                            <div>
                                <p style={{ margin: '5px 0', fontSize: '14px', color: '#000000' }}>
                                    <strong>Prepared By:</strong> SiteKick Technologies, Inc.
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px', color: '#000000' }}>
                                    <strong>Date:</strong> {moment().format('YYYY-MM-DD HH:mm:ss')}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px', color: '#000000' }}>
                                    <strong>Contact:</strong> {reportData.supportEmail}
                                </p>
                            </div>
                            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                            <div style={{ textAlign: 'center' }}>
                                <p style={{ margin: '5px 0', color: '#000000', fontSize: '12px' }}>
                                    © {reportData.companyYear} SiteKick – Reality Capture & Monitoring Solutions
                                </p>
                                <p style={{ margin: '5px 0', textDecoration: 'none', color: '#000000 !important', fontSize: '12px' }}>
                                    {reportData.website} | {reportData.supportEmail} | {reportData.phone}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>}

            {/* Email Modal - Outside report container to avoid CSS conflicts */}
            {emailModalVisible && (
                <Modal
                    title="Send Security Incident Report"
                    open={true}
                    onOk={sendReportEmail}
                    onCancel={() => {
                        console.log('Modal Cancel button clicked');
                        setEmailModalVisible(false);
                    }}
                    okText="Send Email"
                    cancelText="Cancel"
                    confirmLoading={sendingEmail}
                    width={600}
                    zIndex={isEmbedded ? 20000 : 10000}
                    maskClosable={false}
                    getContainer={() => document.body}
                    afterClose={() => console.log('Modal closed')}
                    afterOpenChange={(open) => console.log('Modal afterOpenChange:', open)}
                >
                    <div style={{ marginBottom: '15px' }}>
                        <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                            Email Recipients (comma-separated):
                        </p>
                        <Input.TextArea
                            value={emailRecipients}
                            onChange={(e) => setEmailRecipients(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            rows={4}
                            style={{ fontSize: '14px' }}
                        />
                        <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                            The report will be sent to the email addresses listed above.
                        </p>
                    </div>
                </Modal>
            )}
        </React.Fragment>
    );
};

export default SecurityIncidentReport;
